<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'

export interface ContactFormProps {
  name?: string
}

withDefaults(defineProps<ContactFormProps>(), {
  name: 'Contact'
})

const emit = defineEmits(['submit'])

const isSending = ref(false)
const state = reactive({
  name: '',
  email: '',
  message: ''
})
const rules = {
  name: { required },
  email: { required, email },
  message: { required }
}
const v$ = useVuelidate(rules, state)
// Form submission
async function submitForm() {
  const isFormInvalid = await v$.value.$validate()
  if (!isFormInvalid) {
    return
  }
  try {
    isSending.value = true

    await $fetch('/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: encode({
        'form-name': 'Contact',
        ...state
      })
    })

    emit('submit')
  } catch (error) {
    console.error('error', error)
  } finally {
    isSending.value = false
  }
}

function encode(data) {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
    .join('&')
}
</script>

<template>
  <form
    id="contact"
    :name="name"
    novalidate="true"
    class="grid grid-cols-2 gap-4 lg:gap-6"
    data-netlify="true"
    data-netlify-honeypot="bot-field"
    @submit.prevent="submitForm"
  >
    <div class="col-span-full md:col-span-1">
      <FormInput
        v-model="state.name"
        label="Full Name"
        name="name"
        :is-required="true"
        :is-error="v$.name.$error"
        :message="v$.$errors.length > 0 && v$.name.$errors[0]?.$message"
        :is-message-visible="v$.name.$error"
        :message-fixed="true"
        @change="v$.name.$touch"
      />
    </div>
    <div class="col-span-full md:col-span-1">
      <FormInput
        v-model="state.email"
        type="email"
        label="Email"
        name="email"
        :is-required="true"
        :is-error="v$.email.$error"
        :message="v$.$errors.length > 0 && v$.email.$errors[0]?.$message"
        :is-message-visible="v$.email.$error"
        :message-fixed="true"
        @change="v$.email.$touch"
      />
    </div>
    <div class="col-span-full">
      <FormTextarea
        v-model="state.message"
        class="min-h-20"
        label="Message"
        name="message"
        :is-required="true"
        :is-error="v$.message.$error"
        :message="v$.$errors.length > 0 && v$.message.$errors[0]?.$message"
        :is-message-visible="v$.message.$error"
        :message-fixed="true"
        @change="v$.message.$touch"
      />
    </div>
    <div class="col-span-full mt-7 text-center lg:text-right">
      <Button type="submit" size="lg" variant="cta" label="Send message" />
    </div>
  </form>
</template>
